import { RouterProvider, createRouter } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import 'maplibre-gl/dist/maplibre-gl.css';
import './styles/tokens.css';
import './styles/global.css';
import './styles/mobile.css';
import './lib/i18n';
import { LocaleProvider, applyInitialLocale } from './lib/locale';
import { applyInitialTheme } from './lib/theme';
import { routeTree } from './routeTree.gen';

// Set data-theme on <html> BEFORE React renders to prevent flash-of-wrong-theme.
applyInitialTheme();
// Same idea for <html lang> — set before paint to keep screen readers correct.
applyInitialLocale();

const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
});

// Belt-and-suspenders for TSR HMR. The router-plugin shim (stripped in
// vite.config.ts at transform time) reads `window.__TSR_ROUTER__`; the pinned
// react-router runtime doesn't expose that global, so we publish it ourselves
// for any tooling that asks. In dev only.
if (import.meta.env.DEV && typeof window !== 'undefined') {
  (window as unknown as { __TSR_ROUTER__: typeof router }).__TSR_ROUTER__ = router;
}

// Final fallback: any HMR update on this entry forces a full reload.
if (import.meta.hot) {
  import.meta.hot.accept(() => import.meta.hot?.invalidate());
}

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('root element not found');

createRoot(rootEl).render(
  <StrictMode>
    <LocaleProvider>
      <RouterProvider router={router} />
    </LocaleProvider>
  </StrictMode>,
);
